import { useEffect, useState } from 'react';
import { OFFER_ACTION_LABELS, OFFER_STATUS_COLORS } from '../constants/offer';
import { PORTAL_BADGE, PORTAL_BTN_SECONDARY } from '../constants/portalUi';
import CandidateHistoryModal from './CandidateHistoryModal';
import OnboardingWorkflowPanel from './OnboardingWorkflowPanel';
import OfferStaffReply from './OfferStaffReply';
import OfferDetailsCard from './OfferDetailsCard';
import InterviewScheduleCard from './InterviewScheduleCard';
import InterviewStaffReply from './InterviewStaffReply';
import InterviewRescheduleApprovals from './InterviewRescheduleApprovals';
import InterviewRoundsPanel from './InterviewRoundsPanel';
import { appStageDisplayLabel } from '../utils/interviewRoundDisplay';
import { STAGE_LABELS } from '../constants/hiring';

const STAGE_COLORS = {
  submitted: 'bg-[hsl(var(--ds-primary)/0.12)] text-[hsl(var(--ds-primary))]',
  selected: 'bg-cyan-100 text-cyan-800',
  interview: 'bg-yellow-100 text-yellow-700',
  shortlisted: 'bg-[hsl(var(--ds-primary)/0.12)] text-[hsl(var(--ds-primary))]',
  offer: 'bg-purple-100 text-purple-700',
  joined: 'bg-green-100 text-green-700',
  relieved: 'bg-orange-100 text-orange-700',
  rejected: 'bg-red-100 text-red-700',
  not_selected: 'bg-rose-100 text-rose-800'
};

function EmploymentRecord({ title, children, tone = 'slate' }) {
  const tones = {
    green: 'border-green-200 bg-green-50/40',
    orange: 'border-orange-200 bg-orange-50/40',
    slate: 'border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]'
  };
  return (
    <div className={`border p-3 text-sm space-y-2 ${tones[tone] || tones.slate}`}>
      <p className="font-medium text-[hsl(var(--ds-fg))]">{title}</p>
      {children}
    </div>
  );
}

function AssessmentBlock({ assessment, title }) {
  if (!assessment) return null;
  return (
    <div className="border border-[hsl(var(--ds-border))] p-3 text-sm space-y-2 bg-[hsl(var(--ds-muted))]">
      {title && <p className="font-medium text-[hsl(var(--ds-fg))]">{title}</p>}
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
        <dt className="text-[hsl(var(--ds-muted-fg))]">Recommendation</dt>
        <dd className="capitalize">{assessment.recommendation?.replace('-', ' ') || '—'}</dd>
        <dt className="text-[hsl(var(--ds-muted-fg))]">Summary</dt>
        <dd>{assessment.summary || '—'}</dd>
      </dl>
    </div>
  );
}

function OfferThread({ messages }) {
  if (!messages?.length) return null;
  return (
    <div className="space-y-2 max-h-48 overflow-y-auto">
      {messages.map(msg => (
        <div key={msg.id} className="border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 py-2 text-xs">
          <div className="flex justify-between gap-2 text-[hsl(var(--ds-muted-fg))] mb-1">
            <span className="font-medium text-[hsl(var(--ds-fg))]">
              {OFFER_ACTION_LABELS[msg.action] || msg.action}
              {msg.byRole && ` · ${msg.byRole}`}
            </span>
            <span>{msg.createdAt ? new Date(msg.createdAt).toLocaleString() : ''}</span>
          </div>
          {msg.message && <p className="text-[hsl(var(--ds-fg))] whitespace-pre-wrap">{msg.message}</p>}
          {msg.negotiationRequest && (
            <div className="mt-1 text-[hsl(var(--ds-muted-fg))]">
              {msg.negotiationRequest.expectedPackage && (
                <p>Requested package: {msg.negotiationRequest.expectedPackage}</p>
              )}
              {msg.negotiationRequest.expectedJoiningDate && (
                <p>Requested joining: {msg.negotiationRequest.expectedJoiningDate}</p>
              )}
            </div>
          )}
          {msg.offerDetails?.designation && (
            <p className="mt-1 text-[hsl(var(--ds-muted-fg))] text-[11px]">
              Terms: {msg.offerDetails.designation} · {msg.offerDetails.package}{' '}
              {msg.offerDetails.expectedJoiningDate
                ? `· Join ${msg.offerDetails.expectedJoiningDate}`
                : ''}
            </p>
          )}
        </div>
      ))}
    </div>
  );
}

export default function ApplicationDetailPanel({ app: initialApp, userRole, onClose }) {
  const [app, setApp] = useState(initialApp);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    setApp(initialApp);
  }, [initialApp]);

  const assessments =
    app.interviewAssessments?.length > 0
      ? app.interviewAssessments
      : app.lastInterviewAssessment
        ? [app.lastInterviewAssessment]
        : [];

  const isCandidate = userRole === 'candidate';
  const isStaff = userRole === 'admin' || userRole === 'recruiter' || userRole === 'client';
  const canViewHistory =
    (userRole === 'admin' || userRole === 'recruiter') &&
    app.candidateId &&
    !app.candidateMissing;
  const offerStatus = app.offerStatus || (app.stage === 'offer' ? 'pending' : null);
  const showOnboarding =
    (app.stage === 'offer' && app.offerStatus === 'accepted') ||
    app.stage === 'joined' ||
    (app.stage === 'relieved' && app.joiningRecord);

  return (
    <div
      className="fixed inset-0 z-[250] flex items-center justify-center p-4 bg-black/40"
      onClick={onClose}
    >
      <div
        className="bg-[hsl(var(--ds-surface))] border border-[hsl(var(--ds-border))] shadow-xl rounded-xl max-w-xl w-full max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-[hsl(var(--ds-border))] sticky top-0 bg-[hsl(var(--ds-surface))] z-10 rounded-t-xl">
          <h2 className="font-semibold text-[hsl(var(--ds-fg))]">Application details</h2>
          <div className="flex items-center gap-2 shrink-0">
            {canViewHistory && (
              <button
                type="button"
                className={`${PORTAL_BADGE} border-amber-300 bg-[hsl(var(--ds-warning)/0.15)] text-[hsl(var(--ds-warning-fg))] font-semibold hover:bg-amber-100`}
                onClick={() => setShowHistory(true)}
              >
                History
              </button>
            )}
            <button
              type="button"
              className={`${PORTAL_BTN_SECONDARY} !px-2 !py-1 text-xs`}
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
        <div className="p-4 space-y-4 text-sm">
          <div className="space-y-1">
            <p>
              <span className="text-[hsl(var(--ds-muted-fg))]">ID:</span> {app.id}
            </p>
            <p>
              <span className="text-[hsl(var(--ds-muted-fg))]">Job:</span> {app.jobTitle || app.jobId}
            </p>
            {app.clientName && (
              <p>
                <span className="text-[hsl(var(--ds-muted-fg))]">Client:</span> {app.clientName}
              </p>
            )}
            {!isCandidate && app.candidateName && (
              <p>
                <span className="text-[hsl(var(--ds-muted-fg))]">Candidate:</span> {app.candidateName}
              </p>
            )}
            <p>
              <span className="text-[hsl(var(--ds-muted-fg))]">Stage:</span>{' '}
              <span className={`${PORTAL_BADGE} ${STAGE_COLORS[app.stage] || 'bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))]'}`}>
                {appStageDisplayLabel(app, STAGE_LABELS) || app.stage}
              </span>
            </p>
            {app.stage === 'shortlisted' && (
              <p className="text-sm text-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-primary)/0.06)] border border-[hsl(var(--ds-primary)/0.25)] rounded-lg px-3 py-2">
                Interview completed — candidate is shortlisted and awaiting an offer.
              </p>
            )}
            {app.stage === 'offer' && offerStatus && (
              <p>
                <span className="text-[hsl(var(--ds-muted-fg))]">Offer:</span>{' '}
                <span
                  className={`${PORTAL_BADGE} ${OFFER_STATUS_COLORS[offerStatus] || 'bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))] border-[hsl(var(--ds-border))]'}`}
                >
                  {app.offerStatusLabel || offerStatus}
                </span>
              </p>
            )}
          </div>

          {(app.stage === 'interview' || app.interviewSchedule) && (
            <div className="space-y-3">
              <p className="font-medium text-[hsl(var(--ds-fg))]">Interview</p>
              {app.interviewRounds?.length > 0 && (
                <InterviewRoundsPanel app={app} userRole={userRole} onUpdated={setApp} />
              )}
              {app.interviewSchedule && !app.interviewRounds?.length && (
                <InterviewScheduleCard app={app} />
              )}
              {isStaff && app.interviewSchedule?.status === 'reschedule_requested' && (
                <InterviewRescheduleApprovals
                  app={app}
                  userRole={userRole}
                  onUpdated={setApp}
                />
              )}
              {isStaff && app.interviewSchedule?.status !== 'cancelled' && (
                <InterviewStaffReply app={app} onUpdated={setApp} />
              )}
            </div>
          )}

          {app.stage === 'offer' && (
            <div className="space-y-3">
              <OfferDetailsCard
                offerDetails={app.offerDetails}
                packageSummary={app.offerPackageSummary}
                joiningDateLabel={app.offerJoiningDateLabel}
              />
              {app.candidateNegotiation && isStaff && (
                <div className="border border-amber-200 bg-amber-50/50 p-3 text-xs">
                  <p className="font-medium text-amber-900 mb-1">Latest candidate negotiation</p>
                  {app.candidateNegotiation.expectedPackage && (
                    <p>Package: {app.candidateNegotiation.expectedPackage}</p>
                  )}
                  {app.candidateNegotiation.expectedJoiningDate && (
                    <p>Joining: {app.candidateNegotiation.expectedJoiningDate}</p>
                  )}
                  {app.candidateNegotiation.comments && <p>{app.candidateNegotiation.comments}</p>}
                </div>
              )}
              <p className="font-medium text-[hsl(var(--ds-fg))]">Offer communication</p>
              <OfferThread messages={app.offerMessages} />
              {isStaff && offerStatus !== 'declined' && (
                <OfferStaffReply app={app} onUpdated={setApp} />
              )}
            </div>
          )}

          {showOnboarding && (
            <OnboardingWorkflowPanel app={app} userRole={userRole} onUpdated={setApp} />
          )}

          {(app.joiningRecord || app.stage === 'joined') && app.joiningRecord && (
            <EmploymentRecord title="Joining record" tone="green">
              <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
                <dt className="text-[hsl(var(--ds-muted-fg))]">Joining date</dt>
                <dd>{app.joiningRecord.joiningDate || '—'}</dd>
                {app.joiningRecord.recordedAt && (
                  <>
                    <dt className="text-[hsl(var(--ds-muted-fg))]">Recorded</dt>
                    <dd>{new Date(app.joiningRecord.recordedAt).toLocaleString()}</dd>
                  </>
                )}
              </dl>
              {app.joiningRecord.appointmentLetterUrl && (
                <a
                  href={app.joiningRecord.appointmentLetterUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[hsl(var(--ds-primary))] text-xs underline"
                >
                  View appointment letter
                  {app.joiningRecord.appointmentLetterName
                    ? ` (${app.joiningRecord.appointmentLetterName})`
                    : ''}
                </a>
              )}
            </EmploymentRecord>
          )}

          {app.stage === 'joined' && !app.joiningRecord && (
            <p className="text-xs text-green-800 bg-green-50 border border-green-200 px-3 py-2">
              Candidate has joined. Document uploads and approvals remain available until onboarding is complete.
            </p>
          )}

          {(app.reliefRecord || app.stage === 'relieved') && app.reliefRecord && (
            <EmploymentRecord title="Relief / exit record" tone="orange">
              <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
                <dt className="text-[hsl(var(--ds-muted-fg))]">Date of leaving</dt>
                <dd>{app.reliefRecord.lastWorkingDay || '—'}</dd>
                {app.reliefRecord.daysEmployed != null && (
                  <>
                    <dt className="text-[hsl(var(--ds-muted-fg))]">Days employed</dt>
                    <dd>{app.reliefRecord.daysEmployed}</dd>
                  </>
                )}
                {app.reliefRecord.replacementPeriodDays != null && (
                  <>
                    <dt className="text-[hsl(var(--ds-muted-fg))]">Replacement period</dt>
                    <dd>{app.reliefRecord.replacementPeriodDays} days</dd>
                  </>
                )}
                <dt className="text-[hsl(var(--ds-muted-fg))]">Replacement eligible</dt>
                <dd>
                  {app.reliefRecord.withinReplacementWindow ? (
                    <span className="text-orange-800 font-medium">
                      Yes — position reopened for hiring
                    </span>
                  ) : (
                    <span className="text-[hsl(var(--ds-muted-fg))]">No — outside conditional period</span>
                  )}
                </dd>
              </dl>
              {app.reliefRecord.exitDocumentUrl && (
                <a
                  href={app.reliefRecord.exitDocumentUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[hsl(var(--ds-primary))] text-xs underline"
                >
                  View exit document
                  {app.reliefRecord.exitDocumentName
                    ? ` (${app.reliefRecord.exitDocumentName})`
                    : ''}
                </a>
              )}
              {app.reliefRecord.note && (
                <p className="text-xs text-[hsl(var(--ds-fg))]">
                  <span className="text-[hsl(var(--ds-muted-fg))]">Notes:</span> {app.reliefRecord.note}
                </p>
              )}
            </EmploymentRecord>
          )}

          {app.stage === 'selected' && (
            <div className="border border-cyan-100 bg-cyan-50/40 p-3 text-sm">
              <p className="font-medium text-[hsl(var(--ds-fg))] mb-1">Selected for interview</p>
              <p className="text-[hsl(var(--ds-muted-fg))]">
                Candidate is approved to proceed — schedule an interview from Update stage when ready.
              </p>
              {(app.displayStatusNotes || app.statusNotes)?.trim() && (
                <p className="mt-2 text-[hsl(var(--ds-fg))]">
                  <span className="text-[hsl(var(--ds-muted-fg))]">Notes:</span>{' '}
                  {app.displayStatusNotes || app.statusNotes}
                </p>
              )}
              {app.selectedAt && (
                <p className="text-xs text-[hsl(var(--ds-muted-fg))] mt-1">
                  Selected on {new Date(app.selectedAt).toLocaleString()}
                </p>
              )}
            </div>
          )}

          {app.stage === 'not_selected' && (
            <div className="border border-rose-100 bg-rose-50/40 p-3">
              <p className="font-medium text-[hsl(var(--ds-fg))] mb-1">Not selected</p>
              <p>
                <span className="text-[hsl(var(--ds-muted-fg))]">Reason:</span>{' '}
                {app.notSelectedReasonLabel || app.notSelectedReasonCode || '—'}
              </p>
              {(app.notSelectedComment || app.statusNotes) && (
                <p className="mt-1">
                  <span className="text-[hsl(var(--ds-muted-fg))]">Remarks:</span>{' '}
                  {app.notSelectedComment || app.statusNotes}
                </p>
              )}
              {app.notSelectedAt && (
                <p className="text-xs text-[hsl(var(--ds-muted-fg))] mt-1">
                  Recorded on {new Date(app.notSelectedAt).toLocaleString()}
                </p>
              )}
            </div>
          )}

          {app.stage === 'rejected' && (
            <div className="border border-red-100 bg-red-50/40 p-3">
              <p className="font-medium text-[hsl(var(--ds-fg))] mb-1">Rejection</p>
              <p>
                <span className="text-[hsl(var(--ds-muted-fg))]">Reason:</span>{' '}
                {app.rejectionReasonLabel || app.rejectionReasonCode || '—'}
              </p>
              {app.rejectionComment && (
                <p className="mt-1">
                  <span className="text-[hsl(var(--ds-muted-fg))]">Comments:</span> {app.rejectionComment}
                </p>
              )}
            </div>
          )}

          {assessments.length > 0 && (
            <div className="space-y-2">
              <p className="font-medium text-[hsl(var(--ds-fg))]">Interview assessments</p>
              {assessments.map((a, i) => (
                <AssessmentBlock
                  key={a.recordedAt || i}
                  assessment={a}
                  title={assessments.length > 1 ? `Assessment ${i + 1}` : null}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {showHistory && (
        <CandidateHistoryModal
          candidateId={app.candidateId}
          candidateName={app.candidateName}
          onClose={() => setShowHistory(false)}
        />
      )}
    </div>
  );
}
